import React, { useEffect, useState } from "react";
import { PaperPlaneTilt, Spinner, ShieldCheck, Warning, WarningOctagon, Question } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Domain deliverability health card for the Profile page.
 * Pulls /api/email/deliverability and renders a green/yellow/red badge with
 * the underlying bounce/complaint/open rates plus an action tip.
 */
const STATUS_META = {
  green: {
    label: "Healthy",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/40",
    dot: "bg-emerald-400",
    icon: ShieldCheck,
  },
  yellow: {
    label: "Warming up",
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/40",
    dot: "bg-amber-400",
    icon: Warning,
  },
  red: {
    label: "At risk",
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/40",
    dot: "bg-red-400",
    icon: WarningOctagon,
  },
  insufficient: {
    label: "Not enough data",
    color: "text-zinc-400",
    bg: "bg-zinc-800/40",
    border: "border-zinc-700",
    dot: "bg-zinc-500",
    icon: Question,
  },
};

export default function DeliverabilityBadge() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/email/deliverability")
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Unable to load deliverability"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="deliverability-loading"
        className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 flex items-center gap-3"
      >
        <Spinner size={14} className="animate-spin text-orange-500" />
        <div className="text-xs font-mono text-zinc-500">Checking domain health…</div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div
        data-testid="deliverability-error"
        className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <PaperPlaneTilt size={16} weight="duotone" className="text-orange-500" />
          <div className="font-display font-medium text-zinc-100">Deliverability</div>
        </div>
        <div className="text-xs text-zinc-500">{err || "No data"}</div>
      </div>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.insufficient;
  const Icon = meta.icon;

  return (
    <div
      data-testid="deliverability-card"
      data-status={data.status}
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <PaperPlaneTilt size={16} weight="duotone" className="text-orange-500" />
          <div className="font-display font-medium text-zinc-100">Deliverability</div>
        </div>
        <div
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${meta.bg} ${meta.border}`}
          data-testid="deliverability-badge"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} animate-pulse`} />
          <Icon size={12} className={meta.color} weight="fill" />
          <span className={`text-[11px] font-mono uppercase tracking-wider ${meta.color}`}>
            {meta.label}
          </span>
        </div>
      </div>

      <div className="text-sm text-zinc-200 mb-1" data-testid="deliverability-headline">
        {data.headline}
      </div>
      <div className="text-[11px] font-mono text-zinc-500 mb-4" data-testid="deliverability-advice">
        {data.advice}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Metric label="Sent" value={data.totals.sent} testid="deliv-sent" />
        <Metric
          label="Open"
          value={`${data.rates.open_pct}%`}
          testid="deliv-open"
          tone={data.rates.open_pct >= 20 ? "good" : data.rates.open_pct >= 10 ? "warn" : ""}
        />
        <Metric
          label="Reply"
          value={`${data.rates.reply_pct}%`}
          testid="deliv-reply"
          tone={data.rates.reply_pct >= 5 ? "good" : ""}
        />
        <Metric
          label="Bounce"
          value={`${data.rates.bounce_pct}%`}
          testid="deliv-bounce"
          tone={data.rates.bounce_pct >= 5 ? "bad" : data.rates.bounce_pct >= 2 ? "warn" : "good"}
        />
        <Metric
          label="Spam"
          value={`${data.rates.complaint_pct}%`}
          testid="deliv-spam"
          tone={data.rates.complaint_pct >= 0.5 ? "bad" : data.rates.complaint_pct >= 0.1 ? "warn" : "good"}
        />
        <Metric label="Click" value={`${data.rates.click_pct}%`} testid="deliv-click" />
      </div>
      <div className="text-[10px] font-mono text-zinc-600 mt-3">
        Rolling {data.window_days}-day window · Powered by Resend webhooks
      </div>
    </div>
  );
}

function Metric({ label, value, testid, tone }) {
  const toneClass =
    tone === "good"
      ? "text-emerald-400"
      : tone === "warn"
      ? "text-amber-400"
      : tone === "bad"
      ? "text-red-400"
      : "text-zinc-200";
  return (
    <div
      className="bg-zinc-950/60 border border-zinc-800 rounded-sm px-2.5 py-2"
      data-testid={testid}
    >
      <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className={`text-sm tabular-nums font-mono mt-0.5 ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}
